import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../utils/toast';
import {
  Wand2, X, Loader2, Lock, Unlock, Save, RefreshCw, AlertCircle, Copy, Download,
  CheckCircle2, XCircle, Clock, Film, ArrowRight, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ScriptTemplatizerModal — Templatize V3 · Production Bible Generator.
 *
 * Workflow:
 *   1. Generate → POST /script/templatize (two-phase Fable-5 Bible generation)
 *   2. Poll /status until ready. Bible rendered as editable text.
 *   3. Live validation (gates a–d) + duration-budget meter as the user edits.
 *   4. Lock → promotion gate (422 if malformed) → Bible becomes canonical.
 *   5. Parse to Shot Breakdown (Stage 2) → SCxx-SHxx markers → shots inserted.
 */
export default function ScriptTemplatizerModal({
  open,
  onClose,
  projectId,
  authToken,
  onLocked,
}) {
  const [status, setStatus] = useState('never_run');
  const [locked, setLocked] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [originalMarkdown, setOriginalMarkdown] = useState('');
  const [error, setError] = useState('');
  const [scenesCount, setScenesCount] = useState(0);
  const [usage, setUsage] = useState(null);
  const [progress, setProgress] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [llmFallback, setLlmFallback] = useState(null);

  const headers = { Authorization: `Bearer ${authToken}` };
  const debounceRef = useRef(null);
  const fallbackToastedRef = useRef(false);

  // Honest, vendor-neutral label for which engine finished the Bible.
  const fallbackLabel = (fb) => {
    if (!fb) return '';
    if (fb.engine === 'emergent_openai_cross') return 'a cross-model backup engine';
    return 'the backup engine';
  };

  const fetchStatus = useCallback(async () => {
    if (!projectId || !authToken) return null;
    try {
      const res = await axios.get(
        `${API}/projects/${projectId}/script/templatize/status`,
        { headers },
      );
      const d = res.data;
      setStatus(d.status || 'never_run');
      setLocked(!!d.locked);
      setMarkdown(d.markdown || '');
      setOriginalMarkdown(d.markdown || '');
      setScenesCount(d.scenes_count || 0);
      setUsage(d.usage);
      setProgress(d.progress || null);
      setValidation(d.validation || null);
      setError(d.error || '');
      setLlmFallback(d.llm_fallback || null);
      return d;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, authToken]);

  // Live validation of (possibly unsaved) editor content.
  const runValidation = useCallback(async (text) => {
    if (!projectId || !authToken || !text?.trim()) return;
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/script/templatized/validate`,
        { markdown: text },
        { headers },
      );
      setValidation(res.data);
    } catch (e) {
      /* validation is best-effort — never block the editor */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, authToken]);

  useEffect(() => {
    if (open) { setParseResult(null); fetchStatus(); }
  }, [open, fetchStatus]);

  // Poll while processing
  useEffect(() => {
    if (!open || status !== 'processing') return undefined;
    const id = setInterval(() => fetchStatus(), 3000);
    return () => clearInterval(id);
  }, [open, status, fetchStatus]);

  // Debounced re-validation as the user edits.
  useEffect(() => {
    if (status !== 'ready' || !markdown?.trim()) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runValidation(markdown), 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [markdown, status, runValidation]);

  // CD-3 (2026-07-15): if the Bible was completed on a fallback engine
  // (primary key was billing/capacity-blocked), raise ONE amber toast so the
  // operator knows — the generation still succeeded, no billing error shown.
  useEffect(() => {
    if (status === 'ready' && llmFallback?.fell_back && !fallbackToastedRef.current) {
      fallbackToastedRef.current = true;
      toast.warning(
        `Primary generator was unavailable — your Production Bible was completed on ${fallbackLabel(llmFallback)}. No action needed.`,
      );
    }
    if (status !== 'ready') fallbackToastedRef.current = false;
  }, [status, llmFallback]);

  const kickoff = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/projects/${projectId}/script/templatize`, {}, { headers });
      toast.success('Generating your Production Bible — Director AI is imagining every shot…');
      setStatus('processing');
      setParseResult(null);
      fetchStatus();
    } catch (e) {
      toast.error(`Failed to start: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/projects/${projectId}/script/templatized`,
        { markdown },
        { headers },
      );
      toast.success('Edits saved');
      setOriginalMarkdown(markdown);
      runValidation(markdown);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const lock = async () => {
    if (markdown !== originalMarkdown) {
      await save();
    }
    try {
      await axios.post(
        `${API}/projects/${projectId}/script/templatized/lock`,
        {},
        { headers },
      );
      toast.success('Production Bible locked as canonical');
      setLocked(true);
      if (onLocked) onLocked();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 422 && detail?.reasons) {
        setValidation({ passed: false, reasons: detail.reasons, target_shot_count: detail.target_shot_count });
        toast.error('Cannot lock — the Bible failed validation. See the checklist.');
      } else {
        toast.error(`Lock failed: ${typeof detail === 'string' ? detail : e.message}`);
      }
    }
  };

  const unlock = async () => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/script/templatized/unlock`,
        {},
        { headers },
      );
      toast.success('Unlocked — you can edit again');
      setLocked(false);
      setParseResult(null);
    } catch (e) {
      toast.error(`Unlock failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Stage-2: parse the locked Bible into the canonical Shot Breakdown.
  const parseToBreakdown = async () => {
    setParsing(true);
    setParseResult(null);
    try {
      await axios.post(
        `${API}/projects/${projectId}/shot-breakdown/from-templatized`,
        {},
        { headers },
      );
      toast.success('Parsing the Bible into shots (Stage 2)…');
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/projects/${projectId}`, { headers });
          const p = res.data;
          if (!p.breakdown_processing) {
            clearInterval(poll);
            setParsing(false);
            setParseResult({
              inserted: p.breakdown_inserted_count ?? p.breakdown_output_shot_count ?? 0,
              output: p.breakdown_output_shot_count ?? 0,
              incomplete: !!p.breakdown_stage_incomplete,
              error: p.breakdown_error || '',
            });
            if (onLocked) onLocked();
          }
        } catch (_) { /* keep polling */ }
      }, 2500);
      setTimeout(() => { clearInterval(poll); setParsing(false); }, 180000);
    } catch (e) {
      setParsing(false);
      toast.error(`Parse failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const copyToClipboard = async () => {
    if (!markdown) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
      } else {
        const ta = document.createElement('textarea');
        ta.value = markdown;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Copied to clipboard');
    } catch (e) {
      toast.error('Copy failed — your browser may have blocked it.');
    }
  };

  const downloadAsTxt = () => {
    if (!markdown) return;
    try {
      const blob = new Blob([markdown], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `production-bible-${(projectId || 'project').slice(0, 8)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  if (!open) return null;

  const dirty = markdown !== originalMarkdown;
  const isProcessing = status === 'processing';
  const isReady = status === 'ready';
  const isFailed = status === 'failed';
  const budget = validation?.duration_budget;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="templatizer-modal-backdrop"
    >
      <div
        className="w-full max-w-6xl h-[90vh] flex flex-col rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
        style={{ background: 'rgba(15, 22, 38, 0.98)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="templatizer-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <Film className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">Production Bible Generator</div>
              <div className="text-[11px] text-white/50">
                AI-imagined shots · locked EiPi format · honours your exact shot count
                {locked && <span className="ml-2 text-amber-300">· LOCKED (canonical)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {usage?.target_shot_count && (
              <div className="hidden md:block text-[10px] text-white/40 font-mono">
                target {usage.target_shot_count} shots
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="templatizer-close-btn"
            >
              <X className="w-4 h-4 text-white/60" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {(status === 'never_run') && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <Film className="w-12 h-12 text-cyan-400/40 mb-5" />
              <h3 className="text-lg font-semibold text-white mb-2">Generate a Production Bible</h3>
              <p className="text-sm text-white/60 max-w-md leading-relaxed mb-2">
                Director AI reads your script and imagines every shot — globals,
                per-scene Cast/Costume/Corner, and fully-siloed shot blocks — in
                EiPi's locked format, honouring the exact shot count you chose.
              </p>
              <p className="text-[12px] text-cyan-300/80 max-w-md leading-relaxed mb-5">
                Two-phase generation. When you lock it, it becomes canonical and Stage 2
                parses it into your Shot Breakdown.
              </p>
              <button
                onClick={kickoff}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white shadow-lg shadow-cyan-500/20 flex items-center gap-2 transition-all disabled:opacity-50"
                data-testid="templatizer-kickoff-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Generate Production Bible
              </button>
              <div className="mt-3 text-[11px] text-white/40">~2–4 min · two-phase Fable-5 generation</div>
              <a
                href={`${API}/docs/eipi-bible-format.md`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium text-cyan-300/80 hover:text-cyan-200 underline underline-offset-2"
                data-testid="templatizer-download-format-spec"
              >
                <Download className="w-3.5 h-3.5" /> Prefer to author it yourself? Download the locked EiPi Bible format spec
              </a>
            </div>
          )}

          {isProcessing && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mb-4" />
              <div className="text-sm text-white/80 mb-1">Director AI is building your Production Bible…</div>
              <div className="text-[11px] text-white/40 mb-3">
                Phase 1 (skeleton) → Phase 2 (per-scene shot blocks). This takes a few minutes.
              </div>
              {progress?.scenes_total > 0 && (
                <div className="w-64" data-testid="templatizer-progress">
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-cyan-400 transition-all"
                      style={{ width: `${Math.round((progress.scenes_done / progress.scenes_total) * 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-white/40 mt-1">
                    scene {progress.scenes_done} / {progress.scenes_total}
                  </div>
                </div>
              )}
            </div>
          )}

          {isFailed && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <AlertCircle className="w-10 h-10 text-rose-400 mb-4" />
              <div className="text-sm text-white/80 mb-1">Generation failed</div>
              <div className="text-[12px] text-rose-300/80 max-w-md mb-4" data-testid="templatizer-error">{error || 'Unknown error'}</div>
              <button
                onClick={kickoff}
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 border border-cyan-500/30 flex items-center gap-1.5 transition-all"
                data-testid="templatizer-retry-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          )}

          {isReady && (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-black/30 flex-shrink-0">
                <div className="text-[11px] text-white/60 font-mono">
                  {scenesCount} scene{scenesCount !== 1 ? 's' : ''}
                  {dirty && <span className="ml-3 text-amber-300">· unsaved edits</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={copyToClipboard} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all" title="Copy" data-testid="templatizer-copy-btn">
                    <Copy className="w-3.5 h-3.5 text-white/70" />
                  </button>
                  <button onClick={downloadAsTxt} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all" title="Download .txt" data-testid="templatizer-download-txt-btn">
                    <Download className="w-3.5 h-3.5 text-white/70" />
                  </button>
                  <div className="w-px h-5 bg-white/10 mx-0.5" />
                  {!locked && (
                    <>
                      <button onClick={kickoff} className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-white/5 text-white/80 hover:bg-white/10 border border-white/10 flex items-center gap-1.5 transition-all" data-testid="templatizer-rerun-btn">
                        <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                      </button>
                      <button onClick={save} disabled={saving || !dirty} className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 border border-cyan-500/30 flex items-center gap-1.5 transition-all disabled:opacity-50" data-testid="templatizer-save-btn">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                      </button>
                      <button
                        onClick={lock}
                        disabled={validation && !validation.passed}
                        title={validation && !validation.passed ? 'Fix validation errors before locking' : 'Lock as canonical'}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30 flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="templatizer-lock-btn"
                      >
                        <Lock className="w-3.5 h-3.5" /> Lock as canonical
                      </button>
                    </>
                  )}
                  {locked && (
                    <>
                      <button onClick={parseToBreakdown} disabled={parsing} className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 border border-cyan-500/30 flex items-center gap-1.5 transition-all disabled:opacity-50" data-testid="templatizer-parse-stage2-btn">
                        {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                        Parse to Shot Breakdown
                      </button>
                      <button onClick={unlock} className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30 flex items-center gap-1.5 transition-all" data-testid="templatizer-unlock-btn">
                        <Unlock className="w-3.5 h-3.5" /> Unlock to edit
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Validation + duration status strip */}
              {llmFallback?.fell_back && (
                <div className="px-5 py-2 border-b border-amber-400/20 bg-amber-500/10 flex items-center gap-2 flex-shrink-0" data-testid="templatizer-fallback-banner">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                  <span className="text-[12px] text-amber-200">
                    Primary generator was unavailable — this Bible was completed on {fallbackLabel(llmFallback)}. Output is complete; no action needed.
                  </span>
                </div>
              )}
              {/* Validation + duration status strip */}
              {validation && (
                <div className="px-5 py-2.5 border-b border-white/10 bg-black/20 flex-shrink-0 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="templatizer-validation-panel">
                  <div className="flex items-center gap-2 min-w-0">
                    {validation.passed ? (
                      <span className="flex items-center gap-1.5 text-[12px] font-semibold text-emerald-300" data-testid="templatizer-validation-status">
                        <CheckCircle2 className="w-4 h-4" /> All checks passed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-[12px] font-semibold text-rose-300" data-testid="templatizer-validation-status">
                        <XCircle className="w-4 h-4" /> {validation.reasons?.length || 0} issue{(validation.reasons?.length || 0) !== 1 ? 's' : ''} — cannot lock
                      </span>
                    )}
                    <span className="text-[11px] text-white/40 font-mono">
                      {validation.shot_heading_count}/{validation.target_shot_count} shots
                    </span>
                  </div>

                  {/* Duration-budget meter */}
                  {budget?.target_seconds > 0 && (
                    <div className="flex items-center gap-2 flex-1 min-w-0" data-testid="templatizer-duration-meter">
                      <Clock className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                      <div className="flex-1 min-w-[120px] max-w-[280px]">
                        <div className="relative h-2 rounded-full bg-white/10 overflow-hidden">
                          <div className="absolute inset-y-0 bg-white/10" style={{ left: '90%', right: 0 }} />
                          <div
                            className={`h-full transition-all ${budget.in_band ? 'bg-emerald-400' : 'bg-amber-400'}`}
                            style={{ width: `${Math.min(100, budget.pct_of_target)}%` }}
                          />
                        </div>
                      </div>
                      <span className={`text-[11px] font-mono whitespace-nowrap ${budget.in_band ? 'text-emerald-300' : 'text-amber-300'}`}>
                        {budget.sum_seconds}s / {budget.target_seconds}s
                        {!budget.in_band && ' (out of ±10%)'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Failed-reason checklist */}
              {validation && !validation.passed && validation.reasons?.length > 0 && (
                <div className="px-5 py-2 border-b border-white/10 bg-rose-500/5 flex-shrink-0 max-h-24 overflow-y-auto">
                  <ul className="space-y-0.5">
                    {validation.reasons.slice(0, 8).map((r, i) => (
                      <li key={i} className="text-[11px] text-rose-200/90 font-mono flex items-start gap-1.5" data-testid="templatizer-validation-reason">
                        <span className="text-rose-400 mt-0.5">•</span> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Parse result banner */}
              {parseResult && (
                <div className={`px-5 py-2.5 border-b border-white/10 flex-shrink-0 ${parseResult.incomplete || parseResult.error ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`} data-testid="templatizer-parse-result">
                  {parseResult.error ? (
                    <span className="text-[12px] text-rose-300">Stage-2 parse error: {parseResult.error}</span>
                  ) : (
                    <span className={`text-[12px] font-semibold flex items-center gap-1.5 ${parseResult.incomplete ? 'text-amber-300' : 'text-emerald-300'}`}>
                      <CheckCircle2 className="w-4 h-4" />
                      Stage 2 complete — {parseResult.inserted} shots inserted
                      {parseResult.incomplete && ' (stage incomplete — check breakdown)'}
                    </span>
                  )}
                </div>
              )}

              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                readOnly={locked}
                spellCheck={false}
                className="flex-1 min-h-0 w-full px-5 py-4 bg-black/30 text-white/90 text-[13px] font-mono leading-relaxed border-none focus:outline-none resize-none"
                data-testid="templatizer-markdown-editor"
                placeholder="Your Production Bible will appear here…"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
